import { useState, useEffect } from "react";
import { Phone, MessageCircle, ArrowUp, MapPin, ArrowUpRight, Clock } from "lucide-react";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const NAVY_DEEP = "#040A1F";
const GOLD = "#F5A623";
const GOLD_DARK = "#C88B1F";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FONT_MONO = "ui-monospace, 'SF Mono', 'JetBrains Mono', Menlo, monospace";
function useSydneyTime() {
  const [time, setTime] = useState<string>("");
  useEffect(() => {
    const update = () => {
      try {
        const t = new Intl.DateTimeFormat("en-AU", {
          timeZone: "Australia/Sydney",
          hour: "numeric",
          minute: "2-digit",
          hour12: true
        }).format(new Date());
        setTime(t.toUpperCase().replace(/\s/g, " "));
      } catch {
        setTime("");
      }
    };
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, []);
  return time;
}
export default function ReadyToBegin() {
  const sydneyTime = useSydneyTime();
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY
  }} aria-labelledby="ready-to-begin-heading">
      {/* Background gradients */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
      background: `radial-gradient(ellipse at 50% 30%, ${NAVY_LIGHT}99 0%, transparent 60%), radial-gradient(ellipse at 20% 90%, ${NAVY_DEEP} 0%, transparent 60%), radial-gradient(circle at 50% 50%, ${GOLD}08 0%, transparent 70%)`
    }} />

      {/* Subtle grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}66 50%, transparent 100%)`
    }} />

      {/* Concentric rings decoration */}
      <div aria-hidden className="absolute -top-40 -left-40 pointer-events-none opacity-25">
        <div className="w-[500px] h-[500px] rounded-full border" style={{
        borderColor: `${GOLD}20`
      }}>
          <div className="absolute inset-10 rounded-full border" style={{
          borderColor: `${GOLD}26`
        }}>
            <div className="absolute inset-10 rounded-full border" style={{
            borderColor: `${GOLD}33`
          }} />
          </div>
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 md:pt-28 pb-20 md:pb-24">

        {/* === Live status strip === */}
        <div className="flex items-center justify-between gap-6 mb-12 md:mb-16 flex-wrap">
          <div className="inline-flex items-center gap-3 rounded-full border px-4 py-2" style={{
          borderColor: `${GOLD}40`,
          backgroundColor: `${GOLD}0F`
        }}>
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{
              backgroundColor: GOLD
            }} />
              <span className="relative inline-flex h-2 w-2 rounded-full" style={{
              backgroundColor: GOLD
            }} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.22em] uppercase" style={{
            color: GOLD,
            fontFamily: FONT_BODY
          }}>
              Answering Now · Sydney
            </span>
          </div>

          {sydneyTime && <div className="inline-flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" strokeWidth={2.5} style={{
            color: "rgba(255,255,255,0.4)"
          } as React.CSSProperties} />
              <span className="text-[11px] font-bold tracking-[0.18em] tabular-nums" style={{
            color: "rgba(255,255,255,0.55)",
            fontFamily: FONT_MONO
          }}>
                SYD · {sydneyTime}
              </span>
            </div>}
        </div>

        {/* === Eyebrow + opener === */}
        <div className="text-center mb-8 md:mb-10">
          <div className="mb-4 inline-flex items-center gap-3">
            <span className="h-px w-10" style={{
            backgroundColor: `${GOLD}66`
          }} />
            <span className="text-[11px] font-bold tracking-[0.32em] uppercase" style={{
            color: GOLD
          }}>
              One Number · Every Door
            </span>
            <span className="h-px w-10" style={{
            backgroundColor: `${GOLD}66`
          }} />
          </div>

          <h2 id="ready-to-begin-heading" className="font-extrabold text-white max-w-3xl mx-auto" style={{
          fontFamily: FONT_HEAD,
          fontSize: "clamp(1.625rem, 3vw, 2.25rem)",
          lineHeight: 1.15,
          letterSpacing: "-0.02em"
        }}>
            Pick up the phone.{" "}
            <span style={{
            color: GOLD
          }}>
              Door fixed today.
            </span>
          </h2>
        </div>

        {/* === THE PHONE NUMBER AS HERO === */}
        <div className="relative mb-10 md:mb-12">
          <a href="tel:0485000933" aria-label="Call Gold Garage Door on 0485 000 933" className="group relative block text-center">
            {/* Tiny "Call us" tag above */}
            <div className="text-[10px] font-bold tracking-[0.32em] uppercase mb-3 transition-colors duration-300 group-hover:text-[#F5A623]" style={{
            color: "rgba(255,255,255,0.4)"
          }}>
              — Tap to call —
            </div>

            {/* The phone number */}
            <div className="font-extrabold leading-none transition-all duration-300 group-hover:scale-[1.02]" style={{
            fontFamily: FONT_HEAD,
            color: "white",
            fontSize: "clamp(2.75rem, 10vw, 7.5rem)",
            letterSpacing: "-0.03em",
            fontVariantNumeric: "tabular-nums",
            textShadow: `0 0 60px ${GOLD}26`
          }}>
              <span style={{
              color: GOLD
            }}>0468</span>
              <span className="mx-2 md:mx-3">·</span>
              <span style={{
              color: "white"
            }}>688</span>
              <span className="mx-2 md:mx-3">·</span>
              <span style={{
              color: GOLD
            }}>145</span>
            </div>

            {/* Underline that animates on hover */}
            <div aria-hidden className="mx-auto mt-3 md:mt-4 h-[3px] transition-all duration-500 ease-out" style={{
            background: `linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%)`,
            width: "60%",
            maxWidth: "400px"
          }} />

            {/* Phone icon hint */}
            <div className="mt-5 inline-flex items-center justify-center gap-2 text-[12px] font-bold tracking-[0.18em] uppercase transition-colors duration-300 group-hover:text-[#F5A623]" style={{
            color: "rgba(255,255,255,0.5)"
          }}>
              <Phone className="h-3.5 w-3.5" strokeWidth={2.5} />
              We answer 24/7 emergency
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
            </div>
          </a>
        </div>

        {/* === Three secondary paths === */}
        <div className="flex items-center justify-center gap-4 md:gap-6 flex-wrap mb-14 md:mb-16">
          <SecondaryPath href="https://wa.me/61485000933" icon={MessageCircle} label="WhatsApp Us" />
          <PathDivider />
          <SecondaryPath href="#contact-us" icon={ArrowUp} label="Free quote form" />
          <PathDivider />
          <SecondaryPath href="https://maps.app.goo.gl/cRmPoP3fDg61AK6c6" icon={MapPin} label="Bass Hill, NSW" />
        </div>

        {/* === Bottom bar: trust + signature === */}
        <div className="pt-10 border-t flex flex-col md:flex-row items-start md:items-center justify-between gap-6" style={{
        borderColor: "rgba(255,255,255,0.10)"
      }}>
          {/* Trust micro */}
          <div className="flex items-center gap-5 flex-wrap">
            {["No callout fee", "Fixed-price quote", "12-mo warranty", "Fully insured"].map((label, idx, arr) => <div key={label} className="flex items-center gap-5">
                <span className="text-[11px] font-bold tracking-[0.14em] uppercase" style={{
              color: "rgba(255,255,255,0.7)",
              fontFamily: FONT_BODY
            }}>
                  {label}
                </span>
                {idx < arr.length - 1 && <span aria-hidden className="hidden md:block w-1 h-1 rounded-full" style={{
              backgroundColor: `${GOLD}80`
            }} />}
              </div>)}
          </div>

          {/* Family signature */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center justify-center w-10 h-10 rounded-full font-extrabold text-[13px] flex-shrink-0" style={{
            backgroundColor: GOLD,
            color: NAVY,
            fontFamily: FONT_HEAD,
            letterSpacing: "-0.02em"
          }}>
              GG
            </div>
            <div>
              <div className="text-[12px] font-bold text-white" style={{
              fontFamily: FONT_HEAD,
              letterSpacing: "-0.01em"
            }}>
                Gold Garage Door
              </div>
              <div className="text-[9px] font-semibold tracking-[0.18em] uppercase mt-0.5" style={{
              color: "rgba(255,255,255,0.45)"
            }}>
                Family-run · Est. 2009
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}66 50%, transparent 100%)`
    }} />
    </section>;
}
function SecondaryPath({
  href,
  icon: Icon,
  label
}: {
  href: string;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  label: string;
}) {
  return <a href={href} className="group inline-flex items-center gap-2.5 text-[13px] font-bold tracking-[0.12em] uppercase transition-colors duration-200" style={{
    color: "rgba(255,255,255,0.65)",
    fontFamily: FONT_BODY
  }} onMouseEnter={e => e.currentTarget.style.color = GOLD} onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.65)"}>
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full border transition-all duration-300 group-hover:-translate-y-0.5" style={{
      borderColor: "rgba(255,255,255,0.20)",
      backgroundColor: "rgba(255,255,255,0.04)"
    }}>
        <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
      </span>
      {label}
    </a>;
}
function PathDivider() {
  return <span aria-hidden className="hidden sm:block w-1 h-1 rounded-full" style={{
    backgroundColor: `${GOLD}40`
  }} />;
}